import sodium from "libsodium-wrappers-sumo";

export interface EnrollResponse {
  device_id: string;
  fingerprint: string;
  status: string;
}

export interface StatusResponse {
  device_id: string;
  status: string;
  fingerprint: string;
}

export interface ActivateResponse {
  device_id: string;
  conversation_id: string;
  status: string;
  device_jwt: string;
  owner_identity_public_key: string;
  owner_ephemeral_public_key: string | null;
}

export async function enrollDevice(
  apiUrl: string,
  inviteToken: string,
  identityPkHex: string,
  ephemeralPkHex: string,
  proofHex: string,
  platform?: string,
  expectedFramework?: string,
): Promise<EnrollResponse> {
  const res = await fetch(`${apiUrl}/api/v1/enroll`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      invite_token: inviteToken,
      identity_public_key: identityPkHex,
      ephemeral_public_key: ephemeralPkHex,
      proof_of_possession: proofHex,
      platform: platform ?? "node",
      ...(expectedFramework ? { expected_framework: expectedFramework } : {}),
      // #1331: activateDevice() below always signs activation with this
      // identity key, so promise it. Once set, the server refuses an
      // unsigned activation for this device — a stolen device_id alone can
      // no longer be turned into a device JWT.
      activation_pop: true,
    }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Enrollment failed (${res.status}): ${detail}`);
  }
  return res.json();
}

export async function pollDeviceStatus(
  apiUrl: string,
  deviceId: string,
): Promise<StatusResponse & { rateLimited?: boolean }> {
  const res = await fetch(`${apiUrl}/api/v1/devices/${deviceId}/status`);
  if (res.status === 429) {
    // Rate limited — caller should back off and retry
    return { device_id: deviceId, status: "PENDING", fingerprint: "", rateLimited: true };
  }
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Status poll failed (${res.status}): ${detail}`);
  }
  return res.json();
}

const ACTIVATION_DOMAIN = new TextEncoder().encode("agentvault:activate:v1:");
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * #1331 — the bytes the activation proof signs:
 * `"agentvault:activate:v1:" || device_id (16 raw UUID bytes) || signed_at
 * (8-byte big-endian unsigned)`. Must match the backend's
 * `app/services/activation_pop.py::activation_message` exactly. The domain
 * tag keeps this from ever colliding with the enrolment proof (which signs the
 * bare public key) or any other identity-key signature.
 */
export function activationMessage(deviceId: string, signedAt: number): Uint8Array {
  if (!UUID_RE.test(deviceId)) {
    throw new Error("activationMessage: device_id is not a UUID");
  }
  if (!Number.isSafeInteger(signedAt) || signedAt < 0) {
    throw new Error("activationMessage: signed_at must be a non-negative integer");
  }
  const out = new Uint8Array(ACTIVATION_DOMAIN.length + 16 + 8);
  out.set(ACTIVATION_DOMAIN, 0);
  const uuidHex = deviceId.replace(/-/g, "");
  for (let i = 0; i < 16; i++) {
    out[ACTIVATION_DOMAIN.length + i] = parseInt(uuidHex.slice(i * 2, i * 2 + 2), 16);
  }
  new DataView(out.buffer).setBigUint64(ACTIVATION_DOMAIN.length + 16, BigInt(signedAt), false);
  return out;
}

/**
 * Activate an APPROVED device, proving possession of the identity key it
 * enrolled with (#1331). `identityPrivateKey` is the libsodium Ed25519 secret
 * key (64 bytes: seed || public key). The signature is made fresh on every
 * call — the poll loop may retry, and the server only accepts `signed_at`
 * within 300 s of its own clock.
 */
export async function activateDevice(
  apiUrl: string,
  deviceId: string,
  identityPrivateKey: Uint8Array,
): Promise<ActivateResponse> {
  await sodium.ready;
  const signedAt = Math.floor(Date.now() / 1000);
  const signature = sodium.crypto_sign_detached(
    activationMessage(deviceId, signedAt),
    identityPrivateKey,
  );
  const res = await fetch(`${apiUrl}/api/v1/devices/${deviceId}/activate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      activation_signature: sodium.to_hex(signature),
      signed_at: signedAt,
    }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Activation failed (${res.status}): ${detail}`);
  }
  return res.json();
}
